/**
 * Statistik eines Bereichs (F-02) auf dem Board: erledigte Aufgaben je Woche,
 * Gewohnheitsquote der letzten 30 Tage und das Lebensrad im Verlauf.
 */
import { BarChart3 } from 'lucide-react';

import { Saeulen } from '@/ui/composites/Saeulen';
import { Kennzahl, KennzahlZeile } from '@/ui/composites/Statistik';
import { SkalaLegende } from '@/ui/composites/SkalaStreifen';
import { LebensradStreifen } from '@/modules/lifewheel/components/LebensradVerlauf';
import { useHabitsWithStats } from '@/modules/habits/useHabits';
import { tagesVerlauf } from '@/domain/habits/daily';
import { erledigtJeWoche, gesamtQuote, schnitt } from '@/domain/stats';
import { addDays, formatDe, today } from '@/lib/datetime';
import type { ID, Task } from '@/domain/types';

const WOCHEN = 8;
const TAGE = 30;

export function BereichStatistik({ areaId, tasks }: { areaId: ID; tasks: Task[] }) {
  const heute = today();
  const wochen = erledigtJeWoche(tasks, heute, WOCHEN);
  const verlauf = tagesVerlauf(useHabitsWithStats(false, areaId), heute, TAGE);
  const quote = gesamtQuote(verlauf);
  const geschafft = verlauf.reduce((s, p) => s + p.erledigt, 0);
  const vorgesehen = verlauf.reduce((s, p) => s + p.vorgesehen, 0);

  return (
    <section aria-label="Statistik" className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3.5">
      <h2 className="flex items-center gap-1.5 text-sm font-semibold"><BarChart3 className="size-4 text-ink-faint" />Statistik</h2>

      <KennzahlZeile spalten={2}>
        <Kennzahl label="Erledigt diese Woche" wert={String(wochen.at(-1)!.anzahl)}
          hinweis={`Ø ${schnitt(wochen).toLocaleString('de-DE')} je Woche`} />
        <Kennzahl label={`Gewohnheiten · ${TAGE} Tage`} wert={quote === null ? '–' : `${Math.round(quote * 100)} %`}
          hinweis={quote === null ? 'nichts vorgesehen' : `${geschafft} von ${vorgesehen} geschafft`} />
      </KennzahlZeile>

      <Saeulen
        label={`Erledigte Aufgaben je Woche (${WOCHEN} Wochen)`}
        einheit="erledigt"
        punkte={wochen.map((w) => ({
          key: w.start,
          titel: `KW ${w.kw} (${formatDe(w.start, 'dd.MM.')}–${formatDe(addDays(w.start, 6), 'dd.MM.')})`,
          wert: w.anzahl,
        }))}
        links={`KW ${wochen[0]!.kw}`}
        rechts="diese Woche"
      />

      <div className="flex flex-col gap-2 border-t border-line pt-2.5">
        <LebensradStreifen nurBereich={areaId} />
        <SkalaLegende />
      </div>
    </section>
  );
}

